import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { applyActionCode, getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import AmberPaper from '../components/papers/AmberPaper';
import CircularDeterminate from '../components/common/loading';
import { setupFirebaseAuthentication } from '../helpers/firebaseAuthentication';
import { trackSentryErrorByTag } from '../sentry/common';

export const VERIFY_EMAIL_PATH = '/verify-email';

export const STATUS_VERIFYING = 'verifying';
export const STATUS_VERIFIED = 'verified';
export const STATUS_FAILED = 'failed';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
    maxWidth: 420,
  },
  paper: {
    padding: theme.spacing(5),
    backgroundColor: theme.palette.common.white,
    borderRadius: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  icon: {
    fontSize: '4rem',
    marginBottom: theme.spacing(2),
  },
  verified: {
    color: theme.palette.primary.main,
  },
  failed: {
    color: theme.palette.error.main,
  },
  action: {
    marginTop: theme.spacing(4),
    height: theme.spacing(6),
    fontWeight: 800,
  },
}));

/**
 * Landing page of the email verification link. Firebase hands the link over to
 * this page (see sendVerificationEmail) so it shows in the Amberscript design.
 */
function VerifyEmail({ location, history }) {
  const classes = useStyles();
  const [t] = useTranslation();
  const [status, setStatus] = useState(STATUS_VERIFYING);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(location.search);
    const oobCode = params.get('oobCode');

    const verify = async () => {
      if (params.get('mode') !== 'verifyEmail' || !oobCode) {
        throw new Error('Not an email verification link');
      }
      await setupFirebaseAuthentication();
      await applyActionCode(getAuth(), oobCode);
    };

    verify()
      .then(() => !cancelled && setStatus(STATUS_VERIFIED))
      .catch((error) => {
        // An expired or already used link is expected; anything else is worth a look
        if (error?.code !== 'auth/invalid-action-code') {
          trackSentryErrorByTag(error, 'freelancer-2fa-verify-email');
        }
        if (!cancelled) {
          setStatus(STATUS_FAILED);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  if (status === STATUS_VERIFYING) {
    return (
      <div className={classes.root} data-testid="verify-email-loading">
        <CircularDeterminate size="3.6rem" thickness={3.6} />
      </div>
    );
  }

  const verified = status === STATUS_VERIFIED;
  const Icon = verified ? CheckCircleOutlineIcon : ErrorOutlineIcon;

  return (
    <div className={classes.root}>
      <AmberPaper classes={{ root: classes.paper }}>
        <Icon
          className={`${classes.icon} ${
            verified ? classes.verified : classes.failed
          }`}
        />
        <Typography variant="h5" data-testid="verify-email-title">
          {verified
            ? t('verifyEmail.verifiedTitle')
            : t('verifyEmail.failedTitle')}
        </Typography>
        <Box marginTop={2}>
          <Typography variant="body1">
            {verified ? t('verifyEmail.verifiedText') : t('verifyEmail.failedText')}
          </Typography>
        </Box>
        <Button
          className={classes.action}
          fullWidth
          color="primary"
          variant="contained"
          onClick={() => history.push('/')}
          data-testid="verify-email-continue"
        >
          {t('verifyEmail.goToLogin')}
        </Button>
      </AmberPaper>
    </div>
  );
}

VerifyEmail.propTypes = {
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  history: PropTypes.shape({ push: PropTypes.func }).isRequired,
};

export default VerifyEmail;
